/* The survey editor — KANON `04-edytor.html` and `05-edytor-dodaj-pytanie.html`.
 *
 * ONE column under a 56px bar, centred: the form at the canon's 640px. A fluid column would set a
 * different measure on every laptop, and the canon sets one. The client phone preview that stood
 * beside it was removed on 17 September 2026 (owner) — the column was already centred in this
 * scrollport, so it found the middle of the screen with nothing else to share it with.
 *
 * ── The CONTROLS are `canon.module.css`'s, and that is the whole of this round ────────────────
 * This stylesheet used to re-draw the export's own primitives with its own numbers — a chip at
 * radius 18 against 14, a segment at `0 48px` against `0 14px`, a 36px icon button against 28, a
 * weekday at 36 against 32, and four buttons wearing a shared skin while declaring none of its
 * geometry, so they rendered square at weight 400. The owner compared the running screen with the
 * export on 17 September 2026 and refused it. Every one of those now comes from `canon.module.css`,
 * which the list, the matrix, the queue and the shelf read too. **Nothing below may restate a
 * property that file declares** — a declaration on both sides is settled by chunk order, which
 * differs between the dev server and the build.
 *
 * What is left here is this screen's own LAYOUT: where the column stands and what the sections look
 * like.
 *
 * ── What scrolls, and what does not ─────────────────────────────────────────────────────────
 * The bar is a band and `.columns` is the scrollport under it. That only became true on 17 September
 * 2026: `<main>` used to wear a `.page` of this file's own that declared no height at all, so the
 * DOCUMENT scrolled, `.columns`'s `overflow-y` never engaged, and the form landing grew the page
 * 276px in one frame. The main now wears `shared/ui/canonScreen`'s `.page` — 100dvh and hidden, the
 * bound every other screen in this app already had — and `.body` is the flex column inside it that
 * the entrance animates. Fit-or-scroll from 1280×620 is satisfied by that one vertical scroller.
 *
 * ── The reserve ─────────────────────────────────────────────────────────────────────────────
 * `--h-report-editor-min` holds the form column's shortest drawn shape before its reads land, so
 * the skeleton stands in a box that is already the right size and the answer changes ink rather
 * than geometry. Nothing stands below the column, so it reserves its SHORTER shape and grows into
 * the scroller — the opposite case to a card with content under it.
 *
 * Every value is a token. No literal colour, spacing, radius, shadow, duration or easing. */

/* The bar and the scrollport, inside the bounded page. It exists as a box of its own because the
 * entrance's 14px translate must be CLIPPED: on `<main>` itself — 100dvh tall — the transform
 * overflows the document for the length of the step and a scrollbar appears and goes again. */
.body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

/* ── The top bar ───────────────────────────────────────────────────────────────────────────── */

.bar {
  display: flex;
  flex: none;
  gap: var(--space-4);
  align-items: center;
  height: var(--h-report-editor-bar);
  padding: var(--space-0) var(--space-7);
  border-bottom: var(--border-hairline) solid var(--border);
}

.crumbs {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
  font-size: var(--type-control);
  color: var(--text-2);
}

/* A form still loading has no name to print, so the crumb reserves one rather than borrowing the
 * empty draft's „Nowa ankieta" — that would be a heading claiming a name the form does not have. */
.crumbSkel {
  display: block;
  width: var(--w-report-search);
  height: var(--space-4);
}

.crumbCurrent {
  /* A FIXED height, because the two states fill it differently: a 16px skeleton bar and a line of
   * heading text are not the same box, and the crumb rose 13px when the name landed. Measured. */
  display: flex;
  align-items: center;
  height: var(--h-report-chip);
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--type-title-3);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.barSpacer {
  flex: 1;
}

/* The bar's right-hand slot — a „Wstrzymana" chip and the `⋯`, both of which arrive with the read
 * and must not slide „Zapisz zmiany" when they do. Measured before the reserve existed: the button
 * slid 100px left. The slot is right-aligned, so the room it holds for a chip an active form never
 * draws is absorbed on its left and costs nothing to look at. `min-width` rather than `width`, so
 * an unusually long word grows the slot instead of being clipped. */
.barSlot {
  display: flex;
  flex: none;
  gap: var(--space-4);
  align-items: center;
  justify-content: flex-end;

  /* Its HEIGHT is reserved too. An empty slot is zero tall inside a centred row, so the chip and
   * the `⋯` arriving pushed it 18px — measured, 17 September 2026. */
  height: var(--sz-report-filter);
  min-width: var(--w-report-editor-barslot);
}

/* The labelled save: a 34px pill, its word in 13/600. The fill, the refusal and the focus ring are
 * `shared/ui/emberButton.module.css`'s `.ember.pill`. */
.save {
  height: var(--h-control);
  padding: var(--space-0) var(--space-4);
  font-size: var(--type-control);
  border-radius: var(--r-pill);
}

/* What the save is waiting on, in words beside it: „Niezapisane zmiany" or the refusal itself. */
.saveNote {
  flex: none;
  font-size: var(--type-control);
  color: var(--text-2);
}

/* The one destructive entry in the ⋯ menu, drawn the way the roster's and the client card's are:
 * `--danger-text-strong`, which is the danger ink that clears AA carrying WORDS on a card. */
.menuDestructive {
  color: var(--danger-text-strong);
}

/* ── The column, and the scrollport it is centred in ───────────────────────────────────────── */

/* `scrollbar-gutter: stable both-edges`, and it is a no-jump rule rather than tidiness.
 *
 * The column is CENTRED in this scrollport, so the scrollbar appearing moves it sideways — and
 * whether it appears depends on how many sections the read hands back. `both-edges` rather than
 * plain `stable` precisely because the content is centred: reserving the gutter on one side only
 * moves the centre by half a bar instead of by a whole one, which is a smaller version of the same
 * defect. */
.columns {
  display: flex;
  flex: 1;
  justify-content: center;
  min-height: 0;
  padding: var(--space-6) var(--space-7) var(--space-8);
  overflow-y: auto;
  scrollbar-gutter: stable both-edges;
}

.form {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-4);
  width: var(--w-report-editor-form);
  min-height: var(--h-report-editor-min);
}

/* ── States ────────────────────────────────────────────────────────────────────────────────── */

.state {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: var(--h-report-editor-min);
}

.banner {
  margin: var(--space-0);
  padding: var(--space-3) var(--space-4);
  font-size: var(--fs-note);
  color: var(--danger-ink);
  background: var(--danger-soft);
  border-radius: var(--radius-md);
}

/* `?klient=`'s line: whom the first save assigns this form to. Quiet ink — a fact, not an alert. */
.forClient {
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.skelSection {
  display: flex;
  box-sizing: border-box;
  flex: none;
  flex-direction: column;
  gap: var(--space-2);

  /* FIXED, and this is the whole of "the gate hides the bars, never the box": without it the band
   * was as tall as whatever was inside it, so the five of them grew 154px each the instant the
   * bars were painted and the lowest moved 544px in one frame. Measured, 17 September 2026. */
  height: var(--h-report-editor-skel);
  padding: var(--space-4);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xl);
}

.skelRow {
  height: var(--space-4);
}

.skelWide {
  width: 60%;
}

.skelNarrow {
  width: 35%;
}
